import { Navigate, Route, Routes, useLocation } from 'react-router'
import type { Space } from '@takeit4me/shared'
import { useAuth } from './auth'
import { MOCK } from './api'
import { Layout } from './components/Layout'
import { Loading, ErrorBox, EmptyState } from './components/EmptyState'
import { LoginPage } from './pages/LoginPage'
import { MfaChallengePage, MfaEnrolPage, SetPasswordPage, SsoRequiredPage } from './pages/auth/AuthScreens'
import { SuspendedPage } from './pages/platform/SuspendedPage'
import { Lock } from 'lucide-react'
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'

/** Each space is its own chunk, loaded on first visit. */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(async () => ({ default: (await load())[name] }))
}
const TodayPage = page(() => import('./pages/today/TodayPage'), 'TodayPage')
const AlarmsListPage = page(() => import('./pages/today/AlarmsListPage'), 'AlarmsListPage')
const ExceptionsPage = page(() => import('./pages/exceptions/ExceptionsPage'), 'ExceptionsPage')
const LivePage = page(() => import('./pages/live/LivePage'), 'LivePage')
const CrmRoutes = page(() => import('./pages/crm'), 'CrmRoutes')
const TasksRoutes = page(() => import('./pages/tasks'), 'TasksRoutes')
const StudioRoutes = page(() => import('./pages/studio'), 'StudioRoutes')
const ImproveRoutes = page(() => import('./pages/improve'), 'ImproveRoutes')
const AnalyticsPage = page(() => import('./pages/analytics/AnalyticsPage'), 'AnalyticsPage')
const RotaRoute = page(() => import('./pages/rota/RotaRoute'), 'RotaRoute')
const SettingsRoutes = page(() => import('./pages/settings'), 'SettingsRoutes')
const ConversationsRoutes = page(() => import('./pages/conversations'), 'ConversationsRoutes')
const ReviewRoutes = page(() => import('./pages/review'), 'ReviewRoutes')
const OnboardingPage = page(() => import('./pages/platform/OnboardingPage'), 'OnboardingPage')
const AccountPage = page(() => import('./pages/platform/AccountPage'), 'AccountPage')
const SignupPage = page(() => import('./pages/platform/SignupPage'), 'SignupPage')
const SubprocessorsPage = page(() => import('./pages/platform/SubprocessorsPage'), 'SubprocessorsPage')
const OpsApp = page(() => import('./pages/ops/OpsApp'), 'OpsApp')
const NotificationsPage = page(() => import('./pages/notifications/NotificationsPage'), 'NotificationsPage')
const SearchPage = page(() => import('./pages/search/SearchPage'), 'SearchPage')

function Guard({ space, children }: { space: Space; children: ReactNode }) {
  const { me } = useAuth()
  if (me && !me.spaces.includes(space)) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState icon={<Lock size={20} />} title="Not available for your role">
          Ask an admin if you need access to this space.
        </EmptyState>
      </div>
    )
  }
  return <Suspense fallback={<div className="flex-1"><Loading /></div>}>{children}</Suspense>
}

const full = (el: ReactNode) => <Suspense fallback={<Loading className="h-full" />}>{el}</Suspense>

export function App() {
  const { status, me, meError, reloadMe, signOut, block, mfaChallenge, recovery } = useAuth()
  const loc = useLocation()

  // The operator console has its own sign-in (single sign-on through Cloudflare Access) and shell.
  if (loc.pathname === '/ops' || loc.pathname.startsWith('/ops/')) return full(<Routes><Route path="/ops/*" element={<OpsApp />} /></Routes>)
  // Public: sign-up works signed in or out.
  if (loc.pathname === '/signup') return full(<SignupPage />)
  if (loc.pathname === '/legal/subprocessors') return full(<SubprocessorsPage />)
  if (loc.pathname === '/auth/set-password' || recovery) return <SetPasswordPage />

  if (status === 'loading') return <Loading className="h-full" label="Starting…" />
  if (status === 'signed_out') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />} />
      </Routes>
    )
  }
  // The sign-in does not meet the company's policy yet (design 17.1): finish it before anything else.
  if (mfaChallenge || block?.code === 'mfa_required') return <MfaChallengePage />
  if (block?.code === 'mfa_enrol_required') return <MfaEnrolPage />
  if (block?.code === 'sso_required') return <SsoRequiredPage />
  if (me?.lifecycle?.status === 'suspended' || block?.code === 'company_suspended') return <SuspendedPage />
  if (!me) {
    if (meError) return (
      <div className="h-full flex items-center justify-center">
        <div className="max-w-lg w-full text-center">
          <ErrorBox error={`We couldn't load your workspace: ${meError.message}`} retry={() => void reloadMe()} />
          {!MOCK && <button className="btn" onClick={() => void signOut()}>Sign in as someone else</button>}
        </div>
      </div>
    )
    return <Loading className="h-full" label="Loading your workspace…" />
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to={(loc.state as { from?: string } | null)?.from ?? '/today'} replace />} />
      <Route element={<Layout />}>
        <Route index element={<Navigate to="/today" replace />} />
        <Route path="today" element={<Guard space="today"><TodayPage /></Guard>} />
        <Route path="alarms" element={<Guard space="today"><AlarmsListPage /></Guard>} />
        <Route path="exceptions/:id?" element={<Guard space="exceptions"><ExceptionsPage /></Guard>} />
        <Route path="conversations/*" element={<Guard space="exceptions"><ConversationsRoutes /></Guard>} />
        <Route path="live/:id?" element={<Guard space="live"><LivePage /></Guard>} />
        <Route path="crm/*" element={<Guard space="crm"><CrmRoutes /></Guard>} />
        <Route path="tasks/*" element={<Guard space="tasks"><TasksRoutes /></Guard>} />
        <Route path="studio/*" element={<Guard space="studio"><StudioRoutes /></Guard>} />
        <Route path="improve/*" element={<Guard space="improve"><ImproveRoutes /></Guard>} />
        <Route path="analytics" element={<Guard space="analytics"><AnalyticsPage /></Guard>} />
        <Route path="rota" element={<Suspense fallback={<div className="flex-1"><Loading /></div>}><RotaRoute /></Suspense>} />
        <Route path="settings/*" element={<Guard space="settings"><SettingsRoutes /></Guard>} />
        <Route path="review/*" element={<Guard space="exceptions"><ReviewRoutes /></Guard>} />
        <Route path="onboarding" element={<Guard space="settings"><OnboardingPage /></Guard>} />
        <Route path="account" element={<Guard space="today"><AccountPage /></Guard>} />
        <Route path="search" element={<Suspense fallback={<div className="flex-1"><Loading /></div>}><SearchPage /></Suspense>} />
        <Route path="notifications" element={<Suspense fallback={<div className="flex-1"><Loading /></div>}><NotificationsPage /></Suspense>} />
        <Route path="*" element={<div className="flex-1 flex items-center justify-center"><EmptyState title="Page not found">That link doesn't go anywhere.</EmptyState></div>} />
      </Route>
    </Routes>
  )
}
